import React from "react";
import styled from "styled-components";
import {
  Button,
  Center,
  DropdownMenu,
  Left,
  Right,
  Search,
  Thirds,
  spacing,
  ControlGroup,
  Collapse,
  Expand,
} from "@conserveit/blocks";
import { i18n } from "../../../i18n";
import ToggleSnoozed from "../../../containers/ToggleSnoozed";
import {
  getInstantSearchQueryFromUrl,
  getAllInsightsExportData,
} from "../utils";
import { TimeRange, SearchCB, GroupBy, SortBy } from "../utils/types";
import {
  GROUP_BY_OPTIONS,
  SORT_BY_OPTIONS,
  TIME_RANGE_OPTIONS,
} from "../utils/options";
import { useState as useAnalysisState } from "../../../context/analysis";
import { Exporter } from "../../Exporter";

interface Props {
  searchData: Insite.Insights.ComposedOccurrence[];
  snoozedCount: number;
  currentGroup: GroupBy;
  currentSort: SortBy;
  timeRange: Insite.AppState["betaSelections"]["daysRange"];
  expandAll: boolean;
  onExpand: () => void;
  onSearch: SearchCB;
  initialSearch?: string;
  onTimeRangeChange: (days: TimeRange) => void;
  onGroupChange: (option: GroupBy) => void;
  onSortChange: (option: SortBy) => void;
  searchSessionKey: "monitoringSearch" | "insightsSearch";
  exportRawData?: Insite.Insights.ComposedInsight[][];
}

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

export const ToolBar: React.FunctionComponent<Props> = ({
  searchData,
  snoozedCount,
  currentGroup,
  currentSort,
  timeRange,
  expandAll,
  onExpand,
  onSearch,
  searchSessionKey,
  onTimeRangeChange,
  onGroupChange,
  onSortChange,
  initialSearch,
  exportRawData,
}) => {
  const { viewMode } = useAnalysisState();
  const initial = initialSearch || getInstantSearchQueryFromUrl();

  return (
    <StyledThirds>
      <Left>
        <ControlGroup tight>
          <Button
            icon={expandAll ? <Collapse small /> : <Expand small />}
            appearance="outline"
            thin
            onClick={onExpand}
          >
            {expandAll ? `Collapse All` : `Expand All`}
          </Button>

          {exportRawData && (
            <Exporter
              data-cy="export-all-insights"
              appearance="outline"
              rawData={exportRawData}
              fileName="All-Insights.csv"
              dataFormatter={getAllInsightsExportData}
            >
              Export
            </Exporter>
          )}
        </ControlGroup>
      </Left>
      <Center>
        <Search
          sessionKey={searchSessionKey}
          initial={initial}
          data={searchData}
          onSearch={onSearch}
          defaultField="targetValueString"
          indexId="occurrenceId"
        />
      </Center>
      <Right>
        <DropdownMenu
          right
          trigger="Group By"
          value={currentGroup}
          onSelect={onGroupChange}
          options={GROUP_BY_OPTIONS[viewMode].map(({ value, label, sub }) => ({
            value,
            label: label(i18n),
            sub: sub ? sub(i18n) : label(i18n),
          }))}
        />
        <DropdownMenu
          right
          trigger="Sort By"
          value={currentSort}
          onSelect={onSortChange}
          options={SORT_BY_OPTIONS[viewMode].map(({ value, label, sub }) => ({
            value,
            label: label(i18n),
            sub: sub ? sub(i18n) : label(i18n),
          }))}
        />
        <DropdownMenu
          right
          trigger="History"
          value={timeRange}
          onSelect={onTimeRangeChange}
          options={TIME_RANGE_OPTIONS[viewMode].map(({ value, label }) => ({
            value,
            label: label(i18n),
          }))}
        />
        <ToggleSnoozed count={snoozedCount} />
      </Right>
    </StyledThirds>
  );
};
